import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { storeApi } from "@/lib/storefront";
import ProductCard from "@/components/storefront/ProductCard";
import CategorySidebar from "@/components/storefront/CategorySidebar";
import SEO from "@/components/storefront/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/storefront/JsonLd";
import useStoreRefresh from "@/hooks/useStoreRefresh";

const SORT_OPTIONS = [
  { v: "-created_at", l: "Newest" },
  { v: "price", l: "Price: Low to High" },
  { v: "-price", l: "Price: High to Low" },
  { v: "title", l: "A–Z" },
];

const PER_PAGE = 24;

export default function Catalog({ mode = "all" }) {
  const params = useParams();
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState({ items: [], total: 0, total_pages: 1 });
  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = sp.get("q") || "";
  const explicitSort = sp.get("sort");
  const sort = explicitSort || "-created_at";
  const onSale = sp.get("on_sale") === "true";
  const inStock = sp.get("in_stock") === "true";
  const priceMin = sp.get("price_min") || "";
  const priceMax = sp.get("price_max") || "";
  const brandParam = sp.get("brand");
  const categoryParam = sp.get("category");
  const page = Number(sp.get("page") || 1);

  const slug = mode === "category" ? params.slug : (mode === "brand" ? params.slug : null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const apiParams = { page, per_page: PER_PAGE, sort };
      if (q) apiParams.q = q;
      if (onSale) apiParams.on_sale = true;
      if (inStock) apiParams.in_stock = true;
      if (priceMin) apiParams.price_min = Number(priceMin);
      if (priceMax) apiParams.price_max = Number(priceMax);
      if (mode === "category" && slug) apiParams.category_slug = slug;
      if (mode === "brand" && slug) apiParams.brand_slug = slug;
      if (brandParam && mode !== "brand") apiParams.brand_slug = brandParam;
      if (categoryParam && mode !== "category") apiParams.category_slug = categoryParam;
      const { data } = await storeApi.get("/products", { params: apiParams });
      setProducts(data);
    } catch (err) {
      console.error("Catalog load:", err);
    } finally {
      setLoading(false);
    }
  }, [page, sort, q, onSale, inStock, priceMin, priceMax, mode, slug, brandParam, categoryParam]);

  useEffect(() => { load(); }, [load]);

  // Filter panel categories + brands — refetch on focus so admin changes
  // (new/deleted/renamed categories/brands) propagate when the user returns.
  useStoreRefresh(() => {
    storeApi.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    storeApi.get("/brands").then((r) => setBrands(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === "category" && slug) {
      storeApi.get(`/categories/${slug}`).then((r) => setCategory(r.data)).catch(() => setCategory(null));
    } else setCategory(null);
    if (mode === "brand" && slug) {
      storeApi.get(`/brands/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
    } else setBrand(null);
  }, [mode, slug]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value === null || value === "" || value === false) next.delete(key);
    else next.set(key, String(value));
    if (key !== "page") next.delete("page");
    setSp(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    setSp(next);
  };

  const title = useMemo(() => {
    if (mode === "category" && category) return category.name;
    if (mode === "brand" && brand) return brand.name;
    if (q) return `Search results for "${q}"`;
    if (onSale) return "On sale";
    if (explicitSort === "-created_at" && !q) return "New arrivals";
    return "Best sellers";
  }, [mode, category, brand, q, onSale, explicitSort]);

  return (
    <div data-testid="catalog-page">
      <SEO
        title={title === "Best sellers" ? "Best sellers" : title}
        description={category?.description || brand?.description || `Browse ${title.toLowerCase()} — thoughtfully curated pieces from Luna. Creative Home.`}
      />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        ...(mode === "category" ? [{ name: "Shop", url: "/catalog" }] : []),
        ...(mode === "brand" ? [{ name: "Brands", url: "/brands-list" }] : []),
        { name: title, url: typeof window !== "undefined" ? window.location.pathname : undefined },
      ])} />
      {/* Header — category image hero + subcategory chips when available */}
      {mode === "category" && category?.image ? (
        <section className="relative overflow-hidden border-b border-[#DDD6C5]">
          <div className="relative h-[280px] w-full sm:h-[360px] md:h-[460px]">
            <img
              src={category.image}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60" />
            <div className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-4 py-8 sm:px-6 sm:py-10 md:px-10 md:py-14">
              <nav className="flex flex-wrap items-center gap-y-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 sm:text-[11px] sm:tracking-[0.28em]">
                <Link to="/" className="hover:text-white">Home</Link>
                <span className="mx-1.5 text-white/50 sm:mx-2">/</span>
                <Link to="/catalog" className="hover:text-white">Shop</Link>
                {category.parent && (
                  <>
                    <span className="mx-1.5 text-white/50 sm:mx-2">/</span>
                    <Link to={`/c/${category.parent.slug}`} className="hover:text-white">{category.parent.name}</Link>
                  </>
                )}
                <span className="mx-1.5 text-white/50 sm:mx-2">/</span>
                <span className="text-white">{title}</span>
              </nav>
              <h1 className="mt-3 break-words font-display text-[36px] leading-[1.05] text-white sm:text-5xl md:text-[88px]">{title}</h1>
              {category.description && (
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/85 sm:mt-4 sm:text-base md:text-lg">{category.description}</p>
              )}
              <div className="mt-3 store-nums text-sm font-medium text-white/80">{products.total} items</div>
            </div>
          </div>
        </section>
      ) : (
        <section className="border-b border-[#DDD6C5] bg-[#E8E2D2] py-8 sm:py-10 md:py-14">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 md:px-10">
            <nav className="mb-2 flex flex-wrap items-center gap-y-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#2D2823] sm:text-[11px] sm:tracking-[0.28em]">
              <Link to="/" className="hover:text-black">Home</Link>
              <span className="mx-1.5 sm:mx-2">/</span>
              {mode === "category" && <><Link to="/catalog" className="hover:text-black">Shop</Link><span className="mx-1.5 sm:mx-2">/</span></>}
              {mode === "brand" && <><Link to="/catalog" className="hover:text-black">Brands</Link><span className="mx-1.5 sm:mx-2">/</span></>}
              {mode === "category" && category?.parent && (
                <><Link to={`/c/${category.parent.slug}`} className="hover:text-black">{category.parent.name}</Link><span className="mx-1.5 sm:mx-2">/</span></>
              )}
              <span className="text-black">{title}</span>
            </nav>
            <h1 className="break-words font-display text-[28px] leading-[1.05] tracking-tight text-black sm:text-[36px] md:text-[44px]">
              {title}
            </h1>
            {(category?.description || brand?.description) && (
              <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-[#1F1A14] sm:text-[15px]">{category?.description || brand?.description}</p>
            )}
            <div className="mt-1.5 store-nums text-xs font-medium text-[#1F1A14]">{products.total} items</div>
          </div>
        </section>
      )}

      {/* Subcategory strip — premium tiles when the current category has children */}
      {mode === "category" && category?.children && category.children.length > 0 && (
        <section className="border-b border-[#DDD6C5] bg-[#F2EDDF]" data-testid="subcategory-strip">
          <div className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 sm:py-10 md:px-10 md:py-14">
            <div className="mb-5 flex items-end justify-between gap-4 sm:mb-6 md:mb-10">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2D2823] sm:text-[12px] sm:tracking-[0.32em]">
                  Subcategories
                </div>
                <h2 className="mt-2 font-display text-2xl leading-tight text-black sm:mt-3 sm:text-3xl md:text-[44px]">
                  Shop <span className="font-display-soft">by type</span>
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
              {category.children.map((sc) => (
                <Link
                  key={sc.id}
                  to={`/c/${sc.slug}`}
                  className="group relative block overflow-hidden rounded-lg bg-white ring-1 ring-[#D5CDB8] transition-all hover:ring-black"
                  data-testid={`subcat-${sc.slug}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#E8E2D2]">
                    {sc.image && (
                      <img src={sc.image} alt={sc.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.08]" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 p-4 md:p-5">
                    <div className="min-w-0">
                      <div className="truncate font-display text-lg font-medium text-white md:text-xl">{sc.name}</div>
                      {typeof sc.product_count === "number" && sc.product_count > 0 && (
                        <div className="store-nums text-xs font-medium uppercase tracking-[0.2em] text-white/80">{sc.product_count} items</div>
                      )}
                    </div>
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-black" aria-hidden="true">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters + results */}
      <section className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 sm:py-10 md:px-10">
        <div className="md:flex md:gap-10">
          {/* Left rail with categories */}
          <CategorySidebar activeSlug={mode === "category" ? slug : null} />

          {/* Main column */}
          <div className="min-w-0 flex-1">
            <div className="mb-8 flex items-center justify-between gap-3">
              <button
                onClick={() => setFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-[#DDD6C5] bg-white px-4 py-2 text-sm hover:bg-[#E8E2D2]"
                data-testid="open-filters"
              >
                <SlidersHorizontal size={14} /> Filters
              </button>

              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => updateParam("sort", e.target.value)}
                  className="appearance-none rounded-full border border-[#DDD6C5] bg-white px-4 py-2 pr-8 text-sm"
                  data-testid="sort-select"
                >
                  {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:grid-cols-4">
              {loading && Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} className="animate-pulse">
                  <div className="aspect-[4/5] rounded-sm bg-[#E8E2D2]" />
                  <div className="mt-3 h-3 w-1/2 rounded bg-[#DDD6C5]" />
                  <div className="mt-1.5 h-3 w-1/3 rounded bg-[#DDD6C5]" />
                </div>
              ))}
              {!loading && products.items.map((p, i) => <ProductCard key={p.id} product={p} index={i % 4} />)}
            </div>

        {!loading && products.items.length === 0 && (
          <div className="py-20 text-center" data-testid="catalog-empty-state">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#E8E2D2] text-[#7D8471]">
              <SlidersHorizontal size={24} strokeWidth={1.4} />
            </div>
            <div className="font-display text-3xl text-[#1A1A1A]">Nothing <span className="font-display-soft">matches</span></div>
            <p className="mt-3 text-sm text-[#2D2823]">{q ? `We couldn't find anything for "${q}".` : "Try adjusting your filters or browse a different category."}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              <button onClick={clearFilters} className="rounded-full bg-[#1A1A1A] px-6 py-3 text-sm text-white transition-colors hover:bg-[#7D8471]" data-testid="catalog-empty-clear">
                Clear filters
              </button>
              <Link to="/catalog" className="rounded-full border border-[#DDD6C5] bg-white px-6 py-3 text-sm transition-colors hover:bg-[#E8E2D2]">
                Browse all
              </Link>
            </div>
            {categories.length > 0 && (
              <div className="mt-12">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#2D2823]">Or explore a category</div>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {categories.slice(0, 6).map((c) => (
                    <Link key={c.id} to={`/c/${c.slug}`} className="rounded-full border border-[#DDD6C5] bg-white px-4 py-1.5 text-sm transition-colors hover:bg-[#E8E2D2]">
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {products.total_pages > 1 && (
          <div className="mt-14 flex items-center justify-center gap-2" data-testid="pagination">
            <button disabled={page <= 1} onClick={() => updateParam("page", page - 1)} className="rounded-full border border-[#DDD6C5] px-5 py-2 text-sm disabled:opacity-40">Previous</button>
            <span className="px-4 text-sm text-[#2D2823] store-nums">Page {products.page} of {products.total_pages}</span>
            <button disabled={page >= products.total_pages} onClick={() => updateParam("page", page + 1)} className="rounded-full border border-[#DDD6C5] px-5 py-2 text-sm disabled:opacity-40">Next</button>
          </div>
        )}
          </div>
        </div>
      </section>

      {/* Filters drawer */}
      {filtersOpen && (
        <div className="fixed inset-0 z-50" onClick={() => setFiltersOpen(false)}>
          <div className="absolute inset-0 bg-[#1A1A1A]/30" />
          <aside className="absolute right-0 top-0 h-full w-full max-w-sm overflow-y-auto bg-[#F2EDDF] p-6" onClick={(e) => e.stopPropagation()} data-testid="filters-drawer">
            <div className="mb-6 flex items-center justify-between">
              <div className="font-display text-2xl">Filter</div>
              <button onClick={() => setFiltersOpen(false)}><X size={22} /></button>
            </div>

            <div className="space-y-6">
              <div>
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-[#2D2823]">Price</div>
                <div className="flex items-center gap-2">
                  <input type="number" placeholder="Min" value={priceMin} onChange={(e) => updateParam("price_min", e.target.value)} className="w-full rounded-md border border-[#DDD6C5] bg-white px-3 py-2 text-sm store-nums" />
                  <span className="text-[#2D2823]">—</span>
                  <input type="number" placeholder="Max" value={priceMax} onChange={(e) => updateParam("price_max", e.target.value)} className="w-full rounded-md border border-[#DDD6C5] bg-white px-3 py-2 text-sm store-nums" />
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={onSale} onChange={(e) => updateParam("on_sale", e.target.checked)} />
                  On sale
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={inStock} onChange={(e) => updateParam("in_stock", e.target.checked)} />
                  In stock only
                </label>
              </div>

              {mode !== "category" && categories.length > 0 && (
                <div>
                  <div className="mb-2 text-xs uppercase tracking-[0.2em] text-[#2D2823]">Category</div>
                  <div className="space-y-1.5">
                    {categories.slice(0, 12).map((c) => (
                      <label key={c.id} className="flex items-center gap-2 text-sm">
                        <input type="radio" name="cat" checked={categoryParam === c.slug} onChange={() => updateParam("category", c.slug)} />
                        {c.name}
                      </label>
                    ))}
                    {categoryParam && <button onClick={() => updateParam("category", "")} className="mt-1 text-xs text-[#7D8471]">Clear</button>}
                  </div>
                </div>
              )}

              {mode !== "brand" && brands.length > 0 && (
                <div>
                  <div className="mb-2 text-xs uppercase tracking-[0.2em] text-[#2D2823]">Brand</div>
                  <div className="max-h-48 space-y-1.5 overflow-y-auto">
                    {brands.slice(0, 50).map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm">
                        <input type="radio" name="brand" checked={brandParam === b.slug} onChange={() => updateParam("brand", b.slug)} />
                        {b.name}
                      </label>
                    ))}
                    {brandParam && <button onClick={() => updateParam("brand", "")} className="mt-1 text-xs text-[#7D8471]">Clear</button>}
                  </div>
                </div>
              )}

              <button onClick={clearFilters} className="w-full rounded-full border border-[#1A1A1A] px-4 py-2.5 text-sm">Clear all</button>
              <button onClick={() => setFiltersOpen(false)} className="w-full rounded-full bg-[#1A1A1A] px-4 py-3 text-sm text-white hover:bg-[#7D8471]" data-testid="apply-filters">Show {products.total} items</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
